import Guid from "_shared/guids/guid";
import AHDate from "_shared/dates/date";
import MessageFileInfoSchema, { MessageFileInfoJSON, MessageFileInfoParams } from "messaging/attachments/_schemas/message-file-info-schema";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import type { DocumentClassificationStatus } from "documents/classification/document-classification-status";
import { FileScanStatus } from "messaging/attachments/file-info/file-scan-status";
import { MessageAttachmentContentAccess } from "messaging/attachments/file-info/message-attachment-content-access";

export default class MessageFileInfo {
  public static readonly EXAMPLE: MessageFileInfo = new MessageFileInfo({
    id: Guid.generate(),
    name: "example.txt",
    fileSize: 1024,
    mimeType: "text/plain",
    createdDate: new AHDate(),
    rawData: new Blob(["Example content"], { type: "text/plain" }),
    dataUri: "data:text/plain,Example%20content"
  });

  public static create(params: MessageFileInfoParams): MessageFileInfo {
    const parsed = MessageFileInfoSchema.validate(params);
    if (isNullOrUndefined(parsed)) {
      throw new Error("MessageFileInfo.Create(): Invalid parameters.");
    }
    return new MessageFileInfo(parsed);
  }

  public static fromJSON(json: MessageFileInfoJSON): MessageFileInfo {
    if (isNullOrUndefined(json)) return json;
    const params = MessageFileInfoSchema.deserialize(json);
    return new MessageFileInfo(params);
  }

  public static fromJSONArray(jsonArray: MessageFileInfoJSON[]): MessageFileInfo[] {
    return jsonArray.map(json => MessageFileInfo.fromJSON(json));
  }

  public static fromFile(file: File): MessageFileInfo {
    return new MessageFileInfo({
      id: Guid.generate(),
      name: file.name,
      fileSize: file.size,
      mimeType: file.type,
      createdDate: new AHDate(),
      rawData: file,
      dataUri: URL.createObjectURL(file)
    });
  }

  public static toFile(messageFileInfo: MessageFileInfo): File {
    return new File([messageFileInfo.dataUri!], messageFileInfo.name, {
      type: messageFileInfo.mimeType,
    });
  }

  public readonly id: Guid;
  public readonly name: string;
  public readonly fileSize: number;
  public readonly mimeType: string;
  public readonly createdDate: AHDate;
  public readonly rawData?: Blob;
  public readonly dataUri?: string;
  /** The forum the file was shared in, where the list it came from spans forums. */
  public readonly forumId?: Guid;
  /** The message the file was sent with. Set wherever `forumId` is. */
  public readonly messageId?: Guid;
  /**
   * What the malware scan has said about the file (#6916). Absent on a file the client made itself,
   * such as one waiting in the composer, which nobody downloads.
   */
  public readonly scanStatus?: FileScanStatus;
  /** How far classification got (#6877). Undefined where the payload did not say. */
  public readonly classificationStatus?: DocumentClassificationStatus;
  /** The subject matters the classifier found, by name. */
  public readonly subjectMatters?: string[];
  /** Whether the reader may have the file's content, by its subject matters (#6993). Undefined where the payload did not say. */
  public readonly contentAccess?: MessageAttachmentContentAccess;
  /** The subject matters the reader is refused, as the server names them (#6993). Undefined where the payload did not say. */
  public readonly restrictedSubjectMatters?: string[];

  public get isImage(): boolean {
    return this.mimeType.startsWith("image/");
  }

  /**
   * Why the file cannot be downloaded yet, or at all; undefined when it can. The server refuses a
   * download for each of these reasons too, so a Download offered here could only fail.
   *
   * The scan refuses everybody (#6916). The file's subject matters refuse a reader without a grant for
   * one of them (#6993). A payload that says neither leaves the download offered, and the server's
   * refusal then says why.
   */
  public get withheldReason(): string | undefined {
    switch (this.scanStatus) {
      case FileScanStatus.Pending:
        return "Scanning for malware…";
      case FileScanStatus.Infected:
        return "Malware found. It cannot be downloaded.";
      case FileScanStatus.Failed:
        return "Could not be scanned. Upload it again.";
      default:
        return this.subjectMatterRefusal;
    }
  }

  public get isWithheld(): boolean {
    return this.withheldReason !== undefined;
  }

  public constructor(params: MessageFileInfoParams) {
    this.id = params.id;
    this.name = params.name;
    this.fileSize = params.fileSize;
    this.mimeType = params.mimeType;
    this.createdDate = params.createdDate;
    this.rawData = params.rawData;
    this.dataUri = params.dataUri;
    this.forumId = params.forumId;
    this.messageId = params.messageId;
    this.scanStatus = params.scanStatus;
    this.classificationStatus = params.classificationStatus;
    this.subjectMatters = params.subjectMatters;
    this.contentAccess = params.contentAccess;
    this.restrictedSubjectMatters = params.restrictedSubjectMatters;
  }

  /**
   * This file, marked with the forum and the message it was shared in.
   *
   * A file inside a message carries neither id, because the message already names both. A list of
   * files from several forums needs them on each file: the row shows the forum, a download names it
   * in its route, and a realtime update finds the files of one message by `messageId`.
   */
  public sharedIn(forumId: Guid, messageId: Guid): MessageFileInfo {
    return new MessageFileInfo({
      id: this.id,
      name: this.name,
      fileSize: this.fileSize,
      mimeType: this.mimeType,
      createdDate: this.createdDate,
      rawData: this.rawData,
      dataUri: this.dataUri,
      forumId,
      messageId,
      scanStatus: this.scanStatus,
      classificationStatus: this.classificationStatus,
      subjectMatters: this.subjectMatters,
      contentAccess: this.contentAccess,
      restrictedSubjectMatters: this.restrictedSubjectMatters,
    });
  }

  public toJSON(): MessageFileInfoJSON {
    return MessageFileInfoSchema.serialize(this);
  }

  public isEqualTo(other: MessageFileInfo | null | undefined): boolean {
    if (!other) return false;
    return this.id.isEqualTo(other.id);
  }

  public toString(): string {
    return `MessageFileInfo (
      id: ${this.id},
      name: ${this.name},
      fileSize: ${this.fileSize},
      mimeType: ${this.mimeType},
      createdDate: ${this.createdDate.toString()},
      rawData: ${this.rawData ? "[Blob Data]" : "undefined"},
      dataUri: ${this.dataUri ? "[Data URI]" : "undefined"},
      forumId: ${this.forumId?.value ?? "undefined"},
      messageId: ${this.messageId?.value ?? "undefined"},
      scanStatus: ${this.scanStatus ?? "undefined"}
    )`;
  }

  /**
   * Why the reader's subject-matter grants refuse this file (#6993), in the words the server's refusal
   * uses (`AttachmentSubjectMatterRefusal`). It names only the subject matters the reader lacks, which
   * the server sends, so it never sends anyone to ask for a grant they already hold.
   */
  private get subjectMatterRefusal(): string | undefined {
    if (this.contentAccess !== MessageAttachmentContentAccess.SubjectMatterRestricted) return undefined;

    const labels = this.restrictedSubjectMatters?.length ? this.restrictedSubjectMatters.join(" and ") : "sensitive";
    return `This file is ${labels}. Ask a manager for access to ${labels} files.`;
  }
}
